'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  recurringPaymentsApi,
  type RecurringOverviewResponse,
  type RecurringPaymentListResponse,
  type RecurringPaymentQueryOptions,
} from '@/lib/api';
import { useCurrentMonthScope } from '@/hooks/useCurrentMonthScope';
import { calendarScopeQuery } from '@/lib/calendarScope';
import { useAuthedFetcher } from './swrConfig';

export function useRecurringPayments(options?: RecurringPaymentQueryOptions) {
  const authedFetch = useAuthedFetcher();
  const queryKey = buildRecurringPaymentsKeyString('recurring-payments', options);
  const queryOptions = useStableRecurringPaymentOptions(options, queryKey);
  const requestIdRef = useRef(0);
  const dataRef = useRef<RecurringPaymentListResponse | undefined>(undefined);
  const [data, setDataState] = useState<RecurringPaymentListResponse | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  const setData = useCallback((next: RecurringPaymentListResponse | undefined) => {
    dataRef.current = next;
    setDataState(next);
  }, []);

  const fetchLatest = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsValidating(true);
    if (!dataRef.current) setIsLoading(true);

    try {
      const next = await authedFetch((token) => recurringPaymentsApi.getAll(token, queryOptions));
      if (requestIdRef.current === requestId) {
        setData(next);
        setError(undefined);
        setIsLoading(false);
      }
      return next;
    } catch (err) {
      if (requestIdRef.current === requestId) {
        setError(err);
        setIsLoading(false);
      }
      throw err;
    } finally {
      if (requestIdRef.current === requestId) setIsValidating(false);
    }
  }, [authedFetch, queryOptions, setData]);

  useEffect(() => {
    requestIdRef.current += 1;
    setData(undefined);
    setError(undefined);
    setIsLoading(true);
  }, [queryKey, setData]);

  useEffect(() => {
    void fetchLatest().catch(() => {
      // Consumers render errors from the shared finance hook today.
    });
  }, [fetchLatest]);
  useRevalidateOnWindowActivation(fetchLatest);

  const mutate = useCallback(async (
    updater?: RecurringPaymentListResponse
      | ((current: RecurringPaymentListResponse | undefined) => RecurringPaymentListResponse | undefined),
    mutateOptions?: { revalidate?: boolean },
  ) => {
    let nextValue = dataRef.current;
    if (updater !== undefined) {
      nextValue = typeof updater === 'function'
        ? updater(dataRef.current)
        : updater;
      setData(nextValue);
    }

    if (mutateOptions?.revalidate === false) return nextValue;
    return fetchLatest();
  }, [fetchLatest, setData]);

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  };
}

export function useRecurringOverview() {
  const authedFetch = useAuthedFetcher();
  // The overview's "current month" is the CLIENT's calendar (`calendarScopeQuery`): the server
  // clock is UTC and would roll the period hours before the user's month ends. The local month
  // re-triggers the fetch so an open tab rolls over at the local boundary.
  const { year, month } = useCurrentMonthScope();
  const requestIdRef = useRef(0);
  const dataRef = useRef<RecurringOverviewResponse | undefined>(undefined);
  const [data, setDataState] = useState<RecurringOverviewResponse | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  const setData = useCallback((next: RecurringOverviewResponse | undefined) => {
    dataRef.current = next;
    setDataState(next);
  }, []);

  const fetchLatest = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsValidating(true);
    if (!dataRef.current) setIsLoading(true);

    try {
      const next = await authedFetch((token) =>
        recurringPaymentsApi.getOverview(token, calendarScopeQuery()));
      if (requestIdRef.current === requestId) {
        setData(next);
        setError(undefined);
        setIsLoading(false);
      }
      return next;
    } catch (err) {
      if (requestIdRef.current === requestId) {
        setError(err);
        setIsLoading(false);
      }
      throw err;
    } finally {
      if (requestIdRef.current === requestId) setIsValidating(false);
    }
  }, [authedFetch, setData]);

  useEffect(() => {
    void fetchLatest().catch(() => {
      // Consumers render errors from hook state today.
    });
    // year/month: refetch when the local calendar month rolls over.
  }, [fetchLatest, year, month]);
  useRevalidateOnWindowActivation(fetchLatest, 10_000);

  const mutate = useCallback(async (
    updater?: RecurringOverviewResponse
      | ((current: RecurringOverviewResponse | undefined) => RecurringOverviewResponse | undefined),
    mutateOptions?: { revalidate?: boolean },
  ) => {
    let nextValue = dataRef.current;
    if (updater !== undefined) {
      nextValue = typeof updater === 'function'
        ? updater(dataRef.current)
        : updater;
      setData(nextValue);
    }

    if (mutateOptions?.revalidate === false) return nextValue;
    return fetchLatest();
  }, [fetchLatest, setData]);

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  };
}

export function useRecurringPaymentsInfinite(options?: RecurringPaymentQueryOptions) {
  const authedFetch = useAuthedFetcher();
  const queryKey = buildRecurringPaymentsKeyString('recurring-payments-infinite', options);
  const queryOptions = useStableRecurringPaymentOptions(options, queryKey);
  const requestIdRef = useRef(0);
  const pagesRef = useRef<RecurringPaymentListResponse[] | undefined>(undefined);
  const sizeRef = useRef(1);
  const [data, setDataState] = useState<RecurringPaymentListResponse[] | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [size, setSizeState] = useState(1);

  const setData = useCallback((next: RecurringPaymentListResponse[] | undefined) => {
    pagesRef.current = next;
    setDataState(next);
  }, []);

  const fetchPage = useCallback((offset: number) =>
    authedFetch((token) =>
      recurringPaymentsApi.getAll(token, { ...queryOptions, offset })),
  [authedFetch, queryOptions]);

  const fetchPages = useCallback(async (pageCount: number) => {
    const pageSize = queryOptions?.limit ?? 50;
    const pages: RecurringPaymentListResponse[] = [];
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      const previousPage = pages[pageIndex - 1];
      if (previousPage?.hasMore === false) break;
      const offset =
        pageIndex === 0
          ? queryOptions?.offset ?? 0
          : previousPage?.nextOffset ?? pageIndex * pageSize;
      const page = await fetchPage(offset);
      pages.push(page);
    }
    return pages;
  }, [fetchPage, queryOptions?.limit, queryOptions?.offset]);

  const fetchLatest = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsValidating(true);
    if (!pagesRef.current) setIsLoading(true);

    try {
      const next = await fetchPages(sizeRef.current);
      if (requestIdRef.current === requestId) {
        setData(next);
        setError(undefined);
        setIsLoading(false);
      }
      return next;
    } catch (err) {
      if (requestIdRef.current === requestId) {
        setError(err);
        setIsLoading(false);
      }
      throw err;
    } finally {
      if (requestIdRef.current === requestId) setIsValidating(false);
    }
  }, [fetchPages, setData]);

  useEffect(() => {
    requestIdRef.current += 1;
    setData(undefined);
    setError(undefined);
    setIsLoading(true);
    sizeRef.current = 1;
    setSizeState(1);
  }, [queryKey, setData]);

  useEffect(() => {
    void fetchLatest().catch(() => {
      // Consumers render errors from the shared finance hook today.
    });
  }, [fetchLatest]);
  useRevalidateOnWindowActivation(fetchLatest);

  const mutate = useCallback(async (
    updater?: RecurringPaymentListResponse[]
      | ((current: RecurringPaymentListResponse[] | undefined) => RecurringPaymentListResponse[] | undefined),
    mutateOptions?: { revalidate?: boolean },
  ) => {
    let nextValue = pagesRef.current;
    if (updater !== undefined) {
      nextValue = typeof updater === 'function'
        ? updater(pagesRef.current)
        : updater;
      setData(nextValue);
    }

    if (mutateOptions?.revalidate === false) return nextValue;
    return fetchLatest();
  }, [fetchLatest, setData]);

  const setSize = useCallback(async (next: number | ((current: number) => number)) => {
    const targetSize = typeof next === 'function' ? next(sizeRef.current) : next;
    const normalizedSize = Math.max(1, targetSize);
    sizeRef.current = normalizedSize;
    setSizeState(normalizedSize);

    const currentPages = pagesRef.current;
    if (!currentPages) return normalizedSize;
    if (normalizedSize <= currentPages.length) {
      setData(currentPages.slice(0, normalizedSize));
      return normalizedSize;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsValidating(true);

    try {
      const pageSize = queryOptions?.limit ?? 50;
      const pages = [...currentPages];
      while (pages.length < normalizedSize) {
        const previousPage = pages[pages.length - 1];
        if (previousPage?.hasMore === false) break;
        const offset = previousPage?.nextOffset
          ?? ((queryOptions?.offset ?? 0) + pages.length * pageSize);
        pages.push(await fetchPage(offset));
      }

      if (requestIdRef.current === requestId) {
        setData(pages);
        setError(undefined);
        setIsLoading(false);
      }
      return pages.length;
    } catch (err) {
      if (requestIdRef.current === requestId) {
        setError(err);
        setIsLoading(false);
      }
      throw err;
    } finally {
      if (requestIdRef.current === requestId) setIsValidating(false);
    }
  }, [fetchPage, queryOptions?.limit, queryOptions?.offset, setData]);

  return {
    data,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate,
  };
}

function buildRecurringPaymentsKey(
  prefix: string,
  options?: RecurringPaymentQueryOptions,
  offset?: number,
) {
  return [
    prefix,
    Array.isArray(options?.status) ? options.status.join(',') : options?.status ?? null,
    options?.kind ?? null,
    Array.isArray(options?.categoryId) ? options.categoryId.join(',') : options?.categoryId ?? null,
    options?.sortField ?? null,
    options?.sortOrder ?? null,
    options?.upcomingLimit ?? null,
    options?.limit ?? null,
    offset ?? options?.offset ?? 0,
  ] as const;
}

function buildRecurringPaymentsKeyString(
  prefix: string,
  options?: RecurringPaymentQueryOptions,
  offset?: number,
) {
  return JSON.stringify(buildRecurringPaymentsKey(prefix, options, offset));
}

function useStableRecurringPaymentOptions(
  options: RecurringPaymentQueryOptions | undefined,
  queryKey: string,
) {
  // Query identity is value-derived; depending on the raw options object would
  // churn when callers pass equivalent object literals.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => options ? { ...options } : undefined, [queryKey]);
}

function useRevalidateOnWindowActivation(revalidate: () => Promise<unknown>, minIntervalMs = 0) {
  const lastRefreshRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const refresh = () => {
      const now = Date.now();
      if (minIntervalMs > 0 && now - lastRefreshRef.current < minIntervalMs) return;
      lastRefreshRef.current = now;
      void revalidate().catch(() => {
        // The next explicit load will surface the error through hook state.
      });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [minIntervalMs, revalidate]);
}
